import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { from, of } from 'rxjs';
import { catchError, filter, map, mergeMap, tap } from 'rxjs/operators';

import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { notUndefined } from 'app/utils/stream-util';
import GuestController from 'rest/GuestController';
import SocialController from 'rest/SocialController';

import { actions, socialAppSelectors } from '.';

@Injectable()
export class SocialAppEffects extends Loggable {
  private actions$ = inject(Actions);
  private store = inject<Store<AppState>>(Store);

  public constructor() {
    super();
    this.setDisplayName('SocialAppEffects');
  }

  public fetchRoomSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.FETCH_ROOM_SETTINGS_REQ),
      mergeMap(({ socialId }) =>
        from(new SocialController().getSocialSettings(socialId)).pipe(
          map((settings) => actions.FETCH_ROOM_SETTINGS_RSP({ settings })),
          tap({ error: (error: unknown) => this.error('fetchRoomSettings$ error', error) }),
          catchError((error: unknown) => of(actions.FETCH_ROOM_SETTINGS_ERR({ error }))),
        ),
      ),
    );
  });

  public saveRoomSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.SAVE_ROOM_SETTINGS_REQ),
      mergeMap(({ settings, socialId }) =>
        from(new SocialController().saveSocialSettings(socialId, settings)).pipe(
          map(() => actions.SAVE_ROOM_SETTINGS_RSP({ settings })),
          tap({ error: (error: unknown) => this.error('saveRoomSettings$ error', error) }),
          catchError((error: unknown) => of(actions.SAVE_ROOM_SETTINGS_ERR({ error }))),
        ),
      ),
    );
  });

  public updateRoomSet$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.UPDATE_ROOM_SET_REQ),
      mergeMap(({ roomSet }) =>
        from(new SocialController().updateSocialRoomSet(roomSet)).pipe(
          map(() => actions.UPDATE_ROOM_SET_RSP({ roomSet })),
          tap({ error: (error: unknown) => this.error('updateRoomSet$ error', error) }),
          catchError((error: unknown) => of(actions.UPDATE_ROOM_SET_ERR({ error }))),
        ),
      ),
    );
  });

  public updateStartingPoint$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.UPDATE_STARTING_POINT_REQ),
      mergeMap(({ socialRoomSetId, startingPoint }) =>
        from(new SocialController().updateStartingPoint(socialRoomSetId, startingPoint)).pipe(
          map(() => {
            return actions.UPDATE_STARTING_POINT_RSP({ socialRoomSetId, startingPoint });
          }),
          tap({ error: (error: unknown) => this.error('updateStartingPoint$ error', error) }),
          catchError((error: unknown) => of(actions.UPDATE_STARTING_POINT_ERR({ error }))),
        ),
      ),
    );
  });

  public getStartingPoint$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.GET_STARTING_POINT_REQ),
      mergeMap(({ socialRoomSetId }) =>
        from(new SocialController().getStartingPoint(socialRoomSetId)).pipe(
          map((startingPoint) => {
            return actions.GET_STARTING_POINT_RSP({ startingPoint });
          }),
          tap({ error: (error: unknown) => this.error('getStartingPoint$ error', error) }),
          catchError((error: unknown) => of(actions.GET_STARTING_POINT_ERR({ error }))),
        ),
      ),
    );
  });

  /**
   * On room join or start, update any state that requires updated Social entity information, like room set info..
   */
  public startOrJoinRoom$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.JOIN_SOCIAL, actions.START_SOCIAL),
      concatLatestFrom((_action) => this.store.select(socialAppSelectors.getCurrentConnectedRoomSetId)),
      map(([_, setId]) => setId),
      filter(notUndefined),
      tap((setId) => this.info('startOrJoinRoom$ reloading connected room set after room join', { setId })),
      mergeMap((setId) =>
        from(new SocialController().getSocialRoomSet(setId)).pipe(
          map((set) => actions.LOAD_CONNECTED_ROOM_SET({ set })),
          tap({ error: (error: unknown) => this.error('startOrJoinRoom$ error', error) }),
          catchError((error: unknown) => of(actions.LOAD_CONNECTED_ROOM_SET_ERR({ error }))),
        ),
      ),
    );
  });

  public fetchGuestRoomSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.FETCH_GUEST_ROOM_SETTINGS_REQ),
      mergeMap(({ socialId }) =>
        from(new GuestController().getGuestUserRoomSettings(socialId)).pipe(
          map((settings) => actions.FETCH_GUEST_ROOM_SETTINGS_RSP({ settings })),
          tap({ error: (error: unknown) => this.error('fetchGuestRoomSettings$ error', error) }),
          catchError((error: unknown) => of(actions.FETCH_GUEST_ROOM_SETTINGS_ERR({ error }))),
        ),
      ),
    );
  });
}
